import { FC } from 'react';
import {
  DateTimeInput,
  Modal,
  NumberInput,
  TextField,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { useAdminClientUpdateDeposit } from 'store/admin-client-deposits';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { fromCents, toCents } from 'utils/general';

interface Values {
  comment: string;
  invoice: string;
  amount: number;
  date: string;
}

interface Props extends ModalProps {
  depositId: string;
  initialValues: Values;
}

export const EditDepositModal: FC<Props> = ({
  depositId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: {
      ...initialValues,
      amount: fromCents(initialValues.amount),
    },
  });

  const { mutate: updateDeposit, isLoading: isUpdating } =
    useAdminClientUpdateDeposit();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateDeposit(
      {
        depositId,
        data: {
          amount: toCents(values.amount),
          comment: values.comment,
          date: values.date,
          invoice: values.invoice,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('expenses.filters.type.options.deposit')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: !isDirty || isUpdating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="date"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateTimeInput
                label={t('common.date')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="amount"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('employees.amount')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                ref={ref}
                InputProps={{
                  endAdornment: 'EUR',
                }}
              />
            )}
          />

          <Controller
            name="invoice"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('common.invoice_number.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="comment"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('clients.comment')}
                  multiline
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
